import { Eye, PackageCheck } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import type { RecepcionRow } from '../api';

/**
 * Gallery row layout for Recepción. Each row shows compra + proveedor
 * info, expected qty vs received qty, status, and an eye button to
 * open the detail dialog.
 */
export function RecepcionTable({
  rows,
  loading,
  error,
  searchActive,
  onView,
  onRetry,
}: {
  rows: RecepcionRow[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onView: (row: RecepcionRow) => void;
  onRetry: () => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para los filtros aplicados.'
    : 'No hay recepciones registradas.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Status</Th>
              <Th>Recepción</Th>
              <Th>Proveedor</Th>
              <Th>Fecha</Th>
              <Th className="text-right">Esperado</Th>
              <Th className="text-right">Recibido</Th>
              <Th className="w-16 text-right">Ver</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={7} />
            ) : error ? (
              <RowStatus cols={7}>
                <span className="text-destructive">
                  No se pudieron cargar las recepciones.
                </span>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Reintentar
                </Button>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={7}>
                <PackageCheck className="h-5 w-5 text-muted-foreground" aria-hidden />
                {emptyMessage}
              </RowStatus>
            ) : (
              rows.map((r) => <Row key={r.id} row={r} onView={() => onView(r)} />)
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
            <span>No se pudieron cargar las recepciones.</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Reintentar
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <PackageCheck className="h-5 w-5" aria-hidden />
            {emptyMessage}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <RecepcionCard key={r.id} row={r} onView={() => onView(r)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function RecepcionCard({ row, onView }: { row: RecepcionRow; onView: () => void }) {
  const f = row.fields;
  const cantidadEsperada = parseNumber(f.CantidadIngreso_RR);
  const cantidadRecibida = parseNumber(f.CantidadRecibida_RR);
  const ratio =
    cantidadEsperada > 0 ? Math.min(1, cantidadRecibida / cantidadEsperada) : 0;
  return (
    <li
      className="flex flex-col gap-2 px-4 py-3 transition-colors active:bg-muted/40"
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-sm font-medium text-foreground">
          #{row.id} · {row.compra?.proveedor || '—'}
        </span>
        <StatusBadge status={(f.Status_RR ?? '') as string} />
      </div>
      <div className="text-xs text-muted-foreground">
        {row.compra && <>Compra #{row.compra.id} · </>}
        {row.compra?.nroFactura && <>Fc {row.compra.nroFactura} · </>}
        {f.FechaRecepcion_RR || row.compra?.fechaFactura || '—'}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          Esperado: <span className="text-foreground">{cantidadEsperada}</span>{' '}
          · Recibido:{' '}
          <span
            className={cn(
              cantidadRecibida === 0 && 'text-muted-foreground',
              cantidadRecibida > 0 && cantidadRecibida < cantidadEsperada && 'text-amber-700',
              cantidadRecibida >= cantidadEsperada && cantidadEsperada > 0 && 'text-emerald-700',
            )}
          >
            {cantidadRecibida}
          </span>
        </div>
        {cantidadEsperada > 0 && (
          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full transition-all',
                ratio >= 1 ? 'bg-emerald-500' : 'bg-amber-400',
              )}
              style={{ width: `${Math.round(ratio * 100)}%` }}
            />
          </div>
        )}
      </div>
    </li>
  );
}

function Row({ row, onView }: { row: RecepcionRow; onView: () => void }) {
  const f = row.fields;
  const cantidadEsperada = parseNumber(f.CantidadIngreso_RR);
  const cantidadRecibida = parseNumber(f.CantidadRecibida_RR);
  const ratio =
    cantidadEsperada > 0 ? Math.min(1, cantidadRecibida / cantidadEsperada) : 0;
  return (
    <tr className="transition-colors hover:bg-muted/30">
      <Td>
        <StatusBadge status={(f.Status_RR ?? '') as string} />
      </Td>
      <Td className="font-medium text-foreground">#{row.id}</Td>
      <Td>
        <div className="flex flex-col leading-tight">
          <span className="font-medium text-foreground">
            {row.compra?.proveedor || '—'}
          </span>
          <span className="text-xs text-muted-foreground">
            {row.compra ? `Compra #${row.compra.id}` : '—'}
            {row.compra?.nroFactura ? ` · Fc ${row.compra.nroFactura}` : ''}
          </span>
        </div>
      </Td>
      <Td className="text-muted-foreground">
        {f.FechaRecepcion_RR || row.compra?.fechaFactura || '—'}
      </Td>
      <Td className="text-right text-sm tabular-nums">{cantidadEsperada}</Td>
      <Td className="text-right">
        <div className="inline-flex flex-col items-end gap-0.5">
          <span
            className={cn(
              'text-sm tabular-nums',
              cantidadRecibida === 0 && 'text-muted-foreground',
              cantidadRecibida > 0 && cantidadRecibida < cantidadEsperada && 'text-amber-700',
              cantidadRecibida >= cantidadEsperada && cantidadEsperada > 0 && 'text-emerald-700',
            )}
          >
            {cantidadRecibida}
          </span>
          {cantidadEsperada > 0 && (
            <div className="h-1 w-16 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full transition-all',
                  ratio >= 1 ? 'bg-emerald-500' : 'bg-amber-400',
                )}
                style={{ width: `${Math.round(ratio * 100)}%` }}
              />
            </div>
          )}
        </div>
      </Td>
      <Td className="text-right">
        <button
          type="button"
          onClick={onView}
          title="Ver detalle"
          aria-label="Ver detalle"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Eye className="h-4 w-4" aria-hidden />
        </button>
      </Td>
    </tr>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'Pendiente'
      ? 'bg-amber-100 text-amber-700'
      : status === 'Recibido'
        ? 'bg-emerald-100 text-emerald-700'
        : status === 'Anulado'
          ? 'bg-rose-100 text-rose-700'
          : 'bg-secondary text-foreground';
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        cls,
      )}
    >
      {status || '—'}
    </span>
  );
}

function parseNumber(s: string | null | undefined): number {
  if (!s) return 0;
  const n = Number(String(s).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3 align-top', className)}>{children}</td>;
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-3 py-3.5">
              <Skeleton className="h-4 w-full max-w-[120px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
